import { Fragment, memo } from 'react'
import styled from 'styled-components'
import { matchPath } from 'react-router'
import { useLocation } from 'react-router-dom'
import { colors, breakpoints, fontWeights } from '@/styles'
import Icon from '@/components/icon'
import { BreadcrumbList, BreadcrumbAnchor } from './elements'
import { BreadcrumbProps, AnchorProps, BreadcrumbLink } from './types'

const BreadcrumbNav = styled.nav<{ border?: Border }>`
  min-height: 44px;
  width: 100%;
  background: ${colors.white};
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.grey56};
  font-weight: ${fontWeights.medium};
  --active-color: ${colors.black};
  ${props =>
    props.border !== 'none' &&
    `
    border-bottom: 1px solid ${colors.grey};
  `}

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    overflow-x: auto;
  }
`

const ContainerWrap = styled.div<{ align?: Align }>`
  ${props =>
    props.align === 'center' &&
    `
    margin: 0 auto;
  `}
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-width: none;
    padding: 0 20px;
    min-width: 400px;
  }
`

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  text-align: center;
  &[aria-hidden='true'] {
    padding: 0 12px;
  }

  svg {
    color: ${colors.grey56};
    width: 10px;
    transform: rotate(270deg);
  }
`

const Separator = () => <Icon id="chevron" aria-hidden width={3} height={5.4} />

const DummyLink: React.FC<AnchorProps> = ({ 'aria-current': ariaCurrent, to, ...rest }) => (
  <span {...rest} />
)

type Border = 'none' | 'line'
type Align = 'center' | 'left'

type CustomProps = { align?: Align; border?: Border; hideFuture?: boolean }

const isComponent = (node: BreadcrumbLink['label']): node is React.ComponentType<Nothing> => {
  return typeof node === 'function'
}

const Breadcrumbs: React.FC<BreadcrumbProps & CustomProps> = ({
  items: allItems,
  className,
  align = 'center',
  border = 'line',
  hideFuture = false
}) => {
  const location = useLocation()
  const path = location.pathname
  const activeIndex = allItems.findIndex(
    ({ to, label, disabled, exact }) => (!to && !disabled) || matchPath(path, { path: to, exact })
  )
  const items = !hideFuture || activeIndex < 0 ? allItems : allItems.slice(0, activeIndex + 1)
  return (
    <BreadcrumbNav className={className} border={border}>
      <ContainerWrap align={align}>
        <BreadcrumbList justifyContent="space-between">
          {items.map(({ to, label: Label, disabled, exact }, index) => {
            const props: AnchorProps = !!to
              ? { to, exact }
              : { to: '#', tabIndex: -1, isActive: () => true }
            const isLast = index >= items.length - 1
            const toRender = isComponent(Label) ? <Label /> : Label
            return (
              <Fragment key={to}>
                <BreadcrumbItem>
                  <BreadcrumbAnchor
                    {...props}
                    aria-current="step"
                    as={disabled ? DummyLink : undefined}
                  >
                    {toRender}
                  </BreadcrumbAnchor>
                </BreadcrumbItem>
                {!isLast && (
                  <BreadcrumbItem aria-current="page" aria-hidden>
                    <Separator />
                  </BreadcrumbItem>
                )}
              </Fragment>
            )
          })}
        </BreadcrumbList>
      </ContainerWrap>
    </BreadcrumbNav>
  )
}

export default memo(Breadcrumbs)
